import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Button,
  Space,
  Row,
  Col,
  TimePicker,
  message,
  Typography,
  Divider,
} from 'antd';
import { SaveOutlined, GlobalOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { mockSystemConfiguration } from '../data/mockData';
import { TIME_ZONES, DATE_FORMATS, CURRENCIES, LANGUAGES } from '../constants';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const GeneralSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(mockSystemConfiguration);

  React.useEffect(() => {
    form.setFieldsValue({
      ...config,
      businessHours: {
        monday: {
          ...config.businessHours.monday,
          start: config.businessHours.monday.isOpen ? dayjs(config.businessHours.monday.start, 'HH:mm') : null,
          end: config.businessHours.monday.isOpen ? dayjs(config.businessHours.monday.end, 'HH:mm') : null,
        },
        tuesday: {
          ...config.businessHours.tuesday,
          start: config.businessHours.tuesday.isOpen ? dayjs(config.businessHours.tuesday.start, 'HH:mm') : null,
          end: config.businessHours.tuesday.isOpen ? dayjs(config.businessHours.tuesday.end, 'HH:mm') : null,
        },
        wednesday: {
          ...config.businessHours.wednesday,
          start: config.businessHours.wednesday.isOpen ? dayjs(config.businessHours.wednesday.start, 'HH:mm') : null,
          end: config.businessHours.wednesday.isOpen ? dayjs(config.businessHours.wednesday.end, 'HH:mm') : null,
        },
        thursday: {
          ...config.businessHours.thursday,
          start: config.businessHours.thursday.isOpen ? dayjs(config.businessHours.thursday.start, 'HH:mm') : null,
          end: config.businessHours.thursday.isOpen ? dayjs(config.businessHours.thursday.end, 'HH:mm') : null,
        },
        friday: {
          ...config.businessHours.friday,
          start: config.businessHours.friday.isOpen ? dayjs(config.businessHours.friday.start, 'HH:mm') : null,
          end: config.businessHours.friday.isOpen ? dayjs(config.businessHours.friday.end, 'HH:mm') : null,
        },
        saturday: {
          ...config.businessHours.saturday,
          start: config.businessHours.saturday.isOpen ? dayjs(config.businessHours.saturday.start, 'HH:mm') : null,
          end: config.businessHours.saturday.isOpen ? dayjs(config.businessHours.saturday.end, 'HH:mm') : null,
        },
        sunday: {
          ...config.businessHours.sunday,
          start: config.businessHours.sunday.isOpen ? dayjs(config.businessHours.sunday.start, 'HH:mm') : null,
          end: config.businessHours.sunday.isOpen ? dayjs(config.businessHours.sunday.end, 'HH:mm') : null,
        },
      },
    });
  }, [config, form]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // Process business hours
      const processedBusinessHours = Object.keys(values.businessHours).reduce((acc, day) => {
        const dayConfig = values.businessHours[day];
        acc[day] = {
          isOpen: dayConfig.isOpen,
          start: dayConfig.start ? dayConfig.start.format('HH:mm') : '00:00',
          end: dayConfig.end ? dayConfig.end.format('HH:mm') : '00:00',
        };
        return acc;
      }, {} as any);

      const updatedConfig = {
        ...values,
        businessHours: processedBusinessHours,
      };

      setConfig(updatedConfig);
      message.success('General settings updated successfully');
    } catch (error) {
      message.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const businessDays = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
  ];

  return (
    <div>
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* Organization Settings */}
          <Row gutter={[24, 0]}>
            <Col span={24}>
              <Title level={4}>
                <GlobalOutlined /> Organization Settings
              </Title>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                label="Organization Name"
                name="organizationName"
                rules={[{ required: true, message: 'Please enter organization name' }]}
              >
                <Input placeholder="Enter organization name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Time Zone"
                name="timeZone"
                rules={[{ required: true, message: 'Please select time zone' }]}
              >
                <Select placeholder="Select time zone">
                  {TIME_ZONES.map(tz => (
                    <Option key={tz.value} value={tz.value}>
                      {tz.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={8}>
              <Form.Item
                label="Date Format"
                name="dateFormat"
                rules={[{ required: true, message: 'Please select date format' }]}
              >
                <Select placeholder="Select date format">
                  {DATE_FORMATS.map(format => (
                    <Option key={format.value} value={format.value}>
                      {format.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Currency"
                name="currency"
                rules={[{ required: true, message: 'Please select currency' }]}
              >
                <Select placeholder="Select currency">
                  {CURRENCIES.map(currency => (
                    <Option key={currency.value} value={currency.value}>
                      {currency.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Language"
                name="language"
                rules={[{ required: true, message: 'Please select language' }]}
              >
                <Select placeholder="Select language">
                  {LANGUAGES.map(lang => (
                    <Option key={lang.value} value={lang.value}>
                      {lang.flag} {lang.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Operational Settings */}
          <Row gutter={[24, 0]}>
            <Col span={24}>
              <Title level={4}>Operational Settings</Title>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={8}>
              <Form.Item
                label="Max Clients per Coach"
                name="maxClientsPerCoach"
                rules={[{ required: true, message: 'Please enter max clients per coach' }]}
              >
                <InputNumber
                  min={1}
                  max={200}
                  style={{ width: '100%' }}
                  placeholder="Enter max clients"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Default Session Duration (minutes)"
                name="defaultSessionDuration"
                rules={[{ required: true, message: 'Please enter default session duration' }]}
              >
                <InputNumber
                  min={15}
                  max={300}
                  step={15}
                  style={{ width: '100%' }}
                  placeholder="Enter duration"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Maintenance Mode"
                name="maintenanceMode"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Enable to temporarily disable user access
              </Text>
            </Col>
          </Row>

          <Divider />

          {/* Business Hours */}
          <Row gutter={[24, 0]}>
            <Col span={24}>
              <Title level={4}>
                <ClockCircleOutlined /> Business Hours
              </Title>
              <Text type="secondary">
                Configure operating hours for scheduling and availability
              </Text>
            </Col>
          </Row>

          {businessDays.map(day => (
            <Row key={day.key} gutter={[16, 0]} align="middle" style={{ marginBottom: 16 }}>
              <Col span={3}>
                <Text strong>{day.label}</Text>
              </Col>
              <Col span={3}>
                <Form.Item
                  name={['businessHours', day.key, 'isOpen']}
                  valuePropName="checked"
                  style={{ margin: 0 }}
                >
                  <Switch size="small" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={['businessHours', day.key, 'start']}
                  style={{ margin: 0 }}
                >
                  <TimePicker
                    format="HH:mm"
                    placeholder="Start time"
                    style={{ width: '100%' }}
                    disabled={!form.getFieldValue(['businessHours', day.key, 'isOpen'])}
                  />
                </Form.Item>
              </Col>
              <Col span={1} style={{ textAlign: 'center' }}>
                <Text type="secondary">to</Text>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={['businessHours', day.key, 'end']}
                  style={{ margin: 0 }}
                >
                  <TimePicker
                    format="HH:mm"
                    placeholder="End time"
                    style={{ width: '100%' }}
                    disabled={!form.getFieldValue(['businessHours', day.key, 'isOpen'])}
                  />
                </Form.Item>
              </Col>
            </Row>
          ))}

          <Divider />

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
              >
                Save Settings
              </Button>
              <Button onClick={() => form.resetFields()}>
                Reset
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default GeneralSettings; 